import { Link } from "react-router-dom";
import { useSettings } from "@/lib/SettingsContext";
import { CalendarDays, Mail, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SectionEyebrow from "@/components/site/SectionEyebrow";
import { BRAND } from "@/lib/content";

export default function BookConsultationPage() {
  const { settings, loading } = useSettings();
  const url = settings.calendly_url;
  const email = settings.public_contact_email || BRAND.contactEmail;

  return (
    <div data-testid="book-consultation-page">
      <PageHeader
        eyebrow="Book a Consultation"
        title="Pick a time that works."
        lead="A focused 30 minute conversation to understand the work, the room, and the next structured step. No budget questions here. That belongs in the service inquiry."
        testId="book-consultation-header"
      />

      <section className="pb-16">
        <div className="wt-container">
          {loading ? (
            <div
              className="wt-card p-10 text-center wt-text-muted"
              data-testid="booking-loading"
            >
              Loading scheduling…
            </div>
          ) : url ? (
            <div
              data-testid="calendly-embed-wrapper"
              className="wt-card overflow-hidden"
              style={{ minHeight: 720 }}
            >
              <iframe
                src={url}
                title="Calendly scheduling"
                style={{ width: "100%", height: 720, border: 0 }}
              />
            </div>
          ) : (
            <div
              data-testid="calendly-placeholder"
              className="wt-card p-8 md:p-10"
            >
              <SectionEyebrow>Direct line</SectionEyebrow>
              <h2 className="wt-h2 mt-4">Calendar opens shortly.</h2>
              <p className="wt-lead mt-4 max-w-2xl">
                The booking calendar is being connected. In the meantime, the most reliable
                path is a brief email or a service inquiry. Wanakee reviews each submission
                personally and responds with the best next step.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  href={`mailto:${email}?subject=Consultation%20request`}
                  data-testid="booking-email-cta"
                  className="wt-btn-primary"
                >
                  <Mail size={16} aria-hidden="true" /> Email Wanakee
                </a>
                <Link
                  to="/inquiry"
                  data-testid="booking-inquiry-cta"
                  className="wt-btn-outline"
                >
                  <ArrowUpRight size={16} aria-hidden="true" /> Submit an Inquiry
                </Link>
              </div>
              <div className="mt-8 wt-hairline" />
              <p className="mt-6 text-xs wt-text-muted max-w-xl">
                The administrator can paste a Calendly link in Settings and it will appear here
                immediately without any code change.
              </p>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="wt-card p-5">
              <div className="flex items-center gap-2">
                <CalendarDays size={14} style={{ color: "var(--wt-primary)" }} />
                <p className="wt-eyebrow">What to expect</p>
              </div>
              <p className="mt-3 text-sm wt-body">
                A 30 minute call to understand who you serve, what you need, and what the next
                structured step looks like. Bring one specific situation if helpful.
              </p>
            </div>
            <div className="wt-card p-5">
              <div className="flex items-center gap-2">
                <Mail size={14} style={{ color: "var(--wt-primary)" }} />
                <p className="wt-eyebrow">After the call</p>
              </div>
              <p className="mt-3 text-sm wt-body">
                If the work is aligned, Wanakee follows up with the best next step. If it is not,
                she will say so clearly.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
